/**
 * Onboarding — read model for the fresh-org dashboard checklist (turn 12.3).
 *
 * When `get_dashboard` reports `mode: 'onboarding'` (a brand-new org with no
 * documents, tasks, or extra members), `Dashboard.tsx` swaps the populated
 * card stack for the Welcome 5-step checklist. This module owns the static
 * step catalog + the per-step state shape + progress math; `useOnboarding
 * ChecklistVM` owns the mutable state and actions.
 *
 * The step state is in-memory only — it mirrors the spec mock
 * (`../web-mock/features/dashboard/dashboard.html` `renderOnboarding()`),
 * which does not persist done/skipped across reloads. A persisted variant
 * would land with a backing column on `orgs`; not in scope here.
 */

/** The fixed five onboarding steps, in display order. */
export const ONBOARDING_STEP_COUNT = 5;

/** Per-step lifecycle: untouched, completed, or explicitly skipped. */
export type OnboardingStepState = 'todo' | 'done' | 'skipped';

/** One onboarding step definition. */
export interface OnboardingStep {
  /** 1-based step number — also the display order. */
  n: number;
  title: string;
  desc: string;
  /** CTA label on the primary action button. */
  goLabel: string;
  /**
   * Route the "go" action navigates to after marking the step done, or `null`
   * for steps with no destination (step 1 — "Read the docs" — just confirms).
   */
  destination: string | null;
}

/** The canonical onboarding steps — matches the spec mock's `STEPS` array. */
export const ONBOARDING_STEPS: ReadonlyArray<OnboardingStep> = [
  {
    n: 1,
    title: 'Read the docs',
    desc: 'Quick orientation to how Penrose works.',
    goLabel: 'Got it',
    destination: null
  },
  {
    n: 2,
    title: 'Author your first org standard',
    desc: 'Set the global "how we work here" prose every agent will see.',
    goLabel: 'Author',
    destination: '/settings/org-standards'
  },
  {
    n: 3,
    title: 'Define roles & types',
    desc: 'Pick whether to customize function roles + document types or use the defaults.',
    goLabel: 'Configure',
    destination: '/settings/documents'
  },
  {
    n: 4,
    title: 'Create your first document',
    desc: 'A spec, PRD, or note — anything to make the org real.',
    goLabel: 'Create',
    destination: '/documents'
  },
  {
    n: 5,
    title: 'Invite your team',
    desc: "Standards alone aren't enough — bring people in.",
    goLabel: 'Invite',
    destination: '/members'
  }
];

/** Mutable per-step state, keyed by step number (`1`–`5`). */
export type OnboardingStepStates = Record<number, OnboardingStepState>;

/** Every step starts in `todo`. */
export function initialStepStates(): OnboardingStepStates {
  const out: OnboardingStepStates = {};
  for (const step of ONBOARDING_STEPS) out[step.n] = 'todo';
  return out;
}

/** Count of steps in the `done` state. */
export function doneCount(states: OnboardingStepStates): number {
  return ONBOARDING_STEPS.reduce(
    (acc, step) => acc + (states[step.n] === 'done' ? 1 : 0),
    0
  );
}

/** `"2 of 5 done"` progress label. */
export function progressLabel(states: OnboardingStepStates): string {
  return `${doneCount(states)} of ${ONBOARDING_STEP_COUNT} done`;
}
